/**
 * The look of a product's dashboard inside ProTrack.
 *
 * HFO's own site defines its theme in its stylesheet, and this is that theme, so a person moving
 * between the two sees one family: UNAIDS red #E31837 (hover #C8142F, tint #FDEAED), teal #00A99A,
 * near-black #1A1A1A, grey #808284. Any other product gets the neutral indigo it always had, until
 * it is given a theme of its own here.
 *
 * CHART COLOURS are not the brand colours picked by eye. The six below were run through the colour
 * checks (lightness band, chroma floor, colour-blind separation of neighbours, distinctness under
 * normal vision) and pass all of them together; a grey does NOT pass, because it reads as "no
 * colour", so a sixth series is green, not grey. Teal and amber are light on white (under 3:1), so
 * a chart never relies on colour alone: every chart has a legend, direct labels where there is
 * room, and a "Show as table".
 */
const SERIES_UNAIDS = ['#E31837', '#00A99A', '#2563EB', '#F59E0B', '#7C3AED', '#65A30D']
const SERIES_DEFAULT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948']

// MAGNITUDE COLOURS, for the map and the heatmaps: one hue, light to dark, so "darker" can only
// mean "more". Both ramps pass the ordinal checks (lightness rises at every step, one hue, and the
// lightest step still shows against white). A place with no figure is grey, never the lightest
// step, because "nothing sent" and "a small number" are different statements.
const RAMP_UNAIDS = ['#EE97A6', '#E2647B', '#D3304E', '#AC1029', '#6E0B1C']
const RAMP_DEFAULT = ['#A3B1EE', '#7D8CE4', '#5664D6', '#3B43B4', '#232874']
export const NO_DATA = '#E7E5E0'
// The same checks, in the "good" teal: for figures where more is better news (share of data ready).
export const READY_RAMP = ['#5FC4B8', '#22A898', '#008A7D', '#006B61', '#004740']

const UNAIDS = {
  name: 'unaids',
  displayName: 'HIV Financing Observatory (HFO)',
  shortName: 'HFO',
  // Our wording for a screen the product names differently. Matched on the WHOLE title, so a
  // product that renames the screen itself simply stops matching and its own name is shown.
  screenNames: { 'data readiness': 'Data gaps' },
  // Parts of the product's screens we do not show. Country teams sending in missing figures is
  // switched off here for now: the form, the table of what was sent, and the note about it.
  // Matched on the whole title, as screenNames is, so anything new the product adds still shows.
  hidden: { forms: true, tables: ['data sent by country teams'], notes: /send missing data/i },
  accent: '#E31837', accentDark: '#C8142F', accentSoft: '#FDEAED',
  second: '#00A99A', secondSoft: '#E6F7F5',
  ink: '#1A1A1A', muted: '#808284',
  series: SERIES_UNAIDS,
  ramp: RAMP_UNAIDS,
  hero: 'linear-gradient(120deg,#E31837 0%,#C8142F 48%,#8E0F24 100%)',
  tint: 'linear-gradient(135deg,#FDEAED 0%,#FFF7F7 100%)',
}

const NEUTRAL = {
  name: 'neutral',
  accent: '#4F46E5', accentDark: '#4338CA', accentSoft: '#EEF2FF',
  second: '#0EA5E9', secondSoft: '#E0F2FE',
  ink: '#111827', muted: '#6B7280',
  series: SERIES_DEFAULT,
  ramp: RAMP_DEFAULT,
  hero: 'linear-gradient(120deg,#4F46E5 0%,#4338CA 48%,#312E81 100%)',
  tint: 'linear-gradient(135deg,#EEF2FF 0%,#F8FAFF 100%)',
}

const THEMES = { hfo: UNAIDS }

/** What to call the product on a page of ours: its full name where the theme spells one out, else
 *  the name the registry holds. */
export const productLabel = (productKey, name) => THEMES[String(productKey || '').trim().toLowerCase()]?.displayName || name || productKey

/** The short name for a button ("Go to HFO"): the theme's, else the registry's name. */
export const productShortName = (productKey, name) => THEMES[String(productKey || '').trim().toLowerCase()]?.shortName || name || productKey

/** A screen's title as we show it: our wording where the theme has one, else the product's. */
export const screenTitle = (theme, title) => theme?.screenNames?.[String(title || '').trim().toLowerCase()] || title

export const themeFor = (productKey) => THEMES[String(productKey || '').trim().toLowerCase()] || NEUTRAL

/** The theme as CSS variables, set once on the dashboard so any part inside can use it, including
 *  parts that are not handed the theme directly (forms, the usage card). */
export const themeVars = (t) => ({
  '--pt-accent': t.accent, '--pt-accent-dark': t.accentDark, '--pt-accent-soft': t.accentSoft,
  '--pt-second': t.second, '--pt-second-soft': t.secondSoft,
  '--pt-ink': t.ink, '--pt-muted': t.muted, '--pt-tint': t.tint,
})

// ── Reading a figure ──────────────────────────────────────────────────────────

const decimalsOf = (s) => (String(s).split('.')[1] || '').length

/** What kind of figure a stat card holds, so it can be drawn as more than a number:
 *    ratio    "2 of 6" / "2/6"      -> a progress ring
 *    percent  "78.5%"               -> a gauge under the number
 *    number   "781", "$2.49B", "309 mins" -> counts up once
 *    text     anything else, drawn as it is
 *  A date such as "10/14/2026" and a bare year are text: counting up to a year is silly. */
export function parseStat(value) {
  if (value === null || value === undefined || value === '') return { kind: 'empty' }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { kind: 'number', prefix: '', n: value, suffix: '', decimals: decimalsOf(value), commas: false } : { kind: 'text' }
  }
  const s = String(value).trim()
  let m = s.match(/^(\d+(?:\.\d+)?)\s*(?:of|\/)\s*(\d+(?:\.\d+)?)$/i)
  if (m && Number(m[2]) > 0 && Number(m[1]) <= Number(m[2])) {
    return { kind: 'ratio', a: Number(m[1]), b: Number(m[2]) }
  }
  m = s.match(/^(\d+(?:\.\d+)?)\s*%$/)
  if (m && Number(m[1]) <= 100) return { kind: 'percent', n: Number(m[1]), decimals: decimalsOf(m[1]) }
  m = s.match(/^([^\d-]{0,3})(-?\d[\d,]*(?:\.\d+)?)(\s*[^\d].{0,15})?$/)
  if (m && !/\d/.test(m[3] || '') && s.length <= 18) {
    const n = Number(m[2].replace(/,/g, ''))
    const year = Number.isInteger(n) && n >= 1900 && n <= 2100 && !m[1] && !m[3] && !m[2].includes(',')
    if (Number.isFinite(n) && !year) {
      return { kind: 'number', prefix: m[1] || '', n, suffix: m[3] || '', decimals: decimalsOf(m[2]), commas: m[2].includes(',') }
    }
  }
  return { kind: 'text' }
}

// ── Reading a table cell ──────────────────────────────────────────────────────

const GOOD = /^(ready|ok|yes|complete|completed|accepted|available|done|current|fresh|delivered|approved|held|full|active|live)$/i
const WARN = /^(partly|partial|partially|pending|waiting|waiting for review|more information needed|in progress|stale|review|needs review|outdated|limited)$/i
const BAD = /^(blocked|missing|no|none|failed|rejected|not ready|gap|absent|unavailable|overdue)$/i
const MUTE = /^(not used|n\/a|na|unknown)$/i

/** 'good' | 'warn' | 'bad' | 'mute' | null for a WHOLE cell that is a status word. Only an exact
 *  match counts: a sentence that happens to contain "missing" is not a status. */
export function statusKind(cell) {
  if (cell === null || cell === undefined) return null
  const s = String(cell).trim()
  if (!s || s.length > 30) return null
  if (GOOD.test(s)) return 'good'
  if (WARN.test(s)) return 'warn'
  if (BAD.test(s)) return 'bad'
  if (MUTE.test(s)) return 'mute'
  return null
}

export const STATUS_LOOK = {
  good: { bg: '#E6F7F5', fg: '#00695F', dot: '#00A99A' },
  warn: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  bad: { bg: '#FDEAED', fg: '#B80F2B', dot: '#E31837' },
  mute: { bg: '#F3F4F6', fg: '#4B5563', dot: '#9CA3AF' },
}

/** The number in a cell, or null. Letters mean it is not a plain figure ("4.4/5", "2024-25" and
 *  "$1.2B" are left alone, since a column mixing B and M would draw the wrong lengths), so a bar
 *  is only ever drawn behind something that really is a plain number. */
export function cellNumber(cell) {
  if (typeof cell === 'number') return Number.isFinite(cell) ? cell : null
  if (cell === null || cell === undefined) return null
  const s = String(cell).trim().replace(/^[$€£]/, '')
  if (!/^-?\d[\d,]*(\.\d+)?\s*%?$/.test(s)) return null
  const n = Number(s.replace(/[,%\s]/g, ''))
  return Number.isFinite(n) ? n : null
}

/** Which columns of a table are plain figures worth a bar: at least three numeric cells, no text
 *  cells, and not a year, rank or id. Returns {columnIndex: max}. */
export function numericColumns(columns, rows) {
  const out = {}
  const body = Array.isArray(rows) ? rows : []
  const cols = Math.max(Array.isArray(columns) ? columns.length : 0, ...body.map(r => (Array.isArray(r) ? r.length : 1)), 0)
  for (let c = 0; c < cols; c++) {
    const head = String((columns || [])[c] ?? '')
    if (/year|^#$|\bid\b|rank|no\.?$|period|date|week|month/i.test(head)) continue
    const cells = body.map(r => (Array.isArray(r) ? r[c] : c === 0 ? r : null))
    const filled = cells.filter(v => v !== null && v !== undefined && v !== '' && String(v).trim() !== '—')
    const nums = filled.map(cellNumber)
    if (filled.length >= 3 && nums.every(n => n !== null) && nums.some(n => n > 0)) out[c] = Math.max(...nums.map(n => Math.abs(n)))
  }
  return out
}

// ── Reading a table as a grid (the heatmap) ───────────────────────────────────

/** Each column of a table, as the heatmap will draw it: status words, figures (with their range),
 *  or plain text. */
export function heatmapColumns(columns, rows) {
  const cols = Math.max((columns || []).length, ...rows.map(r => (Array.isArray(r) ? r.length : 1)), 0)
  const out = []
  for (let c = 0; c < cols; c++) {
    const head = String((columns || [])[c] ?? '')
    const cells = rows.map(r => (Array.isArray(r) ? r[c] : c === 0 ? r : null))
    const filled = cells.filter(v => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '—')
    if (filled.length >= 2 && filled.every(v => statusKind(v))) { out.push({ c, kind: 'status' }); continue }
    if (/year|^#$|\bid\b|rank|no\.?$|period|date|week|month/i.test(head)) { out.push({ c, kind: 'text' }); continue }
    const nums = filled.map(cellNumber)
    if (filled.length >= 2 && nums.every(n => n !== null)) {
      out.push({ c, kind: 'number', min: Math.min(...nums), max: Math.max(...nums) })
    } else out.push({ c, kind: 'text' })
  }
  return out
}

/** Worth a heatmap: three or more rows, and two or more coloured columns. */
export function canHeatmap(columns, rows) {
  if (!Array.isArray(rows) || rows.length < 3) return false
  return heatmapColumns(columns, rows).filter(k => k.kind !== 'text').length >= 2
}

// ── Reading an amount ─────────────────────────────────────────────────────────

const SCALE = { k: 1e3, thousand: 1e3, m: 1e6, mn: 1e6, million: 1e6, b: 1e9, bn: 1e9, billion: 1e9, t: 1e12, tn: 1e12, trillion: 1e12 }

/** A figure as a product may write it — 1234, "1,234", "$2.49B", "US$ 1.9 billion", "€955M",
 *  "78.5%" — as {n, prefix, percent}, with n at its full size; null for anything else ("4.4/5",
 *  "2024-25", words). Unlike cellNumber, a size word is read, so a column mixing B and M compares
 *  correctly on a map or in a share. */
export function parseAmount(cell) {
  if (typeof cell === 'number') return Number.isFinite(cell) ? { n: cell, prefix: '', percent: false } : null
  if (cell === null || cell === undefined) return null
  const m = String(cell).trim().match(/^(US\$|USD|EUR|GBP|CHF|\$|€|£)?\s*(-?\d[\d,]*(?:\.\d+)?)\s*(k|m|mn|b|bn|t|tn|thousand|million|billion|trillion)?\s*(%)?$/i)
  if (!m) return null
  const n = Number(m[2].replace(/,/g, ''))
  if (!Number.isFinite(n)) return null
  return { n: n * (m[3] ? SCALE[m[3].toLowerCase()] : 1), prefix: m[1] ? (/^usd$/i.test(m[1]) ? 'US$' : m[1]) : '', percent: !!m[4] }
}

/** A figure for a chart label, short: 1,234 · 12.3K · 1.9M · 2.49B, with the currency sign or %
 *  the figures came with. */
export function fmtAmount(n, { prefix = '', percent = false } = {}) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—'
  const v = Number(n)
  if (percent) return `${(Math.round(v * 10) / 10).toLocaleString('en-US')}%`
  const a = Math.abs(v)
  const body = a >= 1e4
    ? new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: a >= 1e9 ? 2 : 1 }).format(v)
    : (Math.round(v * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })
  return `${prefix}${body}`
}

/** Whether figures with this name can be added up — counts, money — rather than being averages,
 *  rates or shares, whose total means nothing. Only an addable figure gets a "share of the whole"
 *  view (regions, mosaic, flow): a region's "average rating" is not the sum of its countries'. */
export const additiveName = (text) => !/\b(avg|average|mean|median|rate|ratio|percent|percentage|share|score|index|rating|per)\b|%/i.test(String(text || ''))
